// eslint-disable-next-line import/no-named-default
import { default as uniqid } from 'uniqid'; // Webpack bundles this incorrectly unless this wacky import syntax is used
import type { App, AppLoaderConfig, AuthConfig, CustomElementApp } from '@nab/x-types';
import { getLoaderConstructor } from '@nab/x-app-loaders';
import type { AppLoader, AppLoaderConstructor } from '@nab/x-app-loaders';
import { clearStrategies } from '@nab/x-auth';
import {
    resetServicesRegistry,
    getUserForApp,
    resetUserRegistry,
    resetActionsRegistry,
    getShellActions,
    getUserForShell,
    getShellServices
} from '@nab/x-registries';
import { runtimeEventBus } from './services/runtimeEventBus';
import { registerInstance, reset as resetInstanceRegistry } from './registries/instanceRegistry';
import { createActionsForApp } from './actions/createActionsForApp';

let sessionId: string = uniqid();
let defaultAuthConfig: AuthConfig = <AuthConfig>{};

export function setSessionId(id: string) {
    sessionId = id;
}
export function getSessionId(): string {
    return sessionId;
}

export function setAuthConfig<C extends AuthConfig>(config: C): void {
    defaultAuthConfig = config;
}

export function getAuthConfig<C extends AuthConfig = AuthConfig>(): C {
    return <C>defaultAuthConfig;
}

/**
 * Starts an app and returns an AppLoader instance allowing you to update or stop the app
 *
 * @todo need to verify arguments.length as hostElement can be undefined if passing in an invalid react ref
 */
export async function loadApp<A extends App = App>(appMetaData: A, hostElement: HTMLElement): Promise<AppLoader<A>>;
export async function loadApp<A extends App = App>(appLoaderConfig: AppLoaderConfig<A>): Promise<AppLoader<A>>;
export async function loadApp<A extends App = App>(...args: [AppLoaderConfig<A>] | [A, HTMLElement]): Promise<AppLoader<A>> {
    if (!getShellActions()) {
        throw new Error(`x-runtime has not been initialised. Please use the initRuntime function before loading apps.`);
    }

    let appLoaderConfig: AppLoaderConfig<A> =
        args.length === 1
            ? args[0]
            : {
                  hostElement: args[1],
                  appMetaData: args[0]
              };

    if (!appLoaderConfig.hostElement || !(appLoaderConfig.hostElement instanceof Element)) {
        throw new Error(`Invalid hostElement supplied for app ${appLoaderConfig.appMetaData.id}. hostElement must be a native DOM Element`);
    }

    appLoaderConfig = {
        ...appLoaderConfig,

        // Each app inherits the root session id and gets it's own unique instance ID
        appConfig: {
            sessionId,
            instanceId: uniqid(),
            ...(appLoaderConfig.appConfig || {})
        },

        // Each app inherits the default auth config for the shell with it's own overrides being applied over the top
        authConfig: {
            ...defaultAuthConfig,

            // The app's metadata is included in it's auth config so auth strategies can reference it (eg. token exchange strategy)
            appMetaData: appLoaderConfig.appMetaData,
            ...(appLoaderConfig.authConfig || {})
        }
    };

    const { tailoring } = getUserForShell();

    /**
     * @todo this isn't ideal as the logic is specific to XV1/XV2 apps... Consider a better approach
     * Note we can't resolve the versionAlias inside the AppLoader as it uses a shell flag rather then a mini-app flag
     */
    if (
        tailoring &&
        (appLoaderConfig.appMetaData as CustomElementApp).assetManifestUrl &&
        (appLoaderConfig.appMetaData as CustomElementApp).versionAlias !== 'NON-EXISTENT'
    ) {
        (appLoaderConfig.appMetaData as CustomElementApp).versionAlias = tailoring.checkFlag<string | null>(
            `miniapp-versionalias-${appLoaderConfig.appMetaData.id}`,
            'NON-EXISTENT'
        );
    }

    const user = getUserForApp({
        appMetaData: appLoaderConfig.appMetaData,
        appConfig: appLoaderConfig.appConfig
    });

    // Register the instance - This stores the config so we can refer to it when exposing public paths, routing events etc..
    registerInstance(appLoaderConfig.appConfig.instanceId, appLoaderConfig);

    // Get an app loader from the registry, this will return the most ideal loading strategy for this specific app
    const Loader: AppLoaderConstructor<A> = getLoaderConstructor<A>(appLoaderConfig.appMetaData, appLoaderConfig.appConfig);
    const loader = new Loader({
        ...appLoaderConfig,

        // Create the actions set for this app instance
        actions: createActionsForApp(appLoaderConfig.appMetaData, appLoaderConfig.appConfig.instanceId),
        services: getShellServices(), // All services to be consumed via User
        user
    });

    // Start the app and return the loader instance so it can be stopped again or data updated if required
    await loader.load();
    return loader;
}

/**
 * Used only for testing. DO NOT EXPOSE!
 */
export function reset() {
    sessionId = uniqid();
    defaultAuthConfig = <AuthConfig>{};
    clearStrategies();
    resetInstanceRegistry();
    resetUserRegistry();
    resetActionsRegistry();
    resetServicesRegistry();
    runtimeEventBus.reset();
}
